import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import {
  Calculator, Search, Loader2, AlertCircle, IndianRupee, Percent, HandCoins,
  Save, ScanLine, Beaker, Pill, BedDouble, ClipboardList, ArrowRight, User, Calendar,
} from 'lucide-react';

const fmtINR = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round(n * 100) / 100;

// Section-level calculator input widget
function SectionCalc({ title, icon, badge, rawFields, calcState, onChange, testIdPrefix }) {
  const { method, percentage, goodwill } = calcState;
  const netForPct = rawFields.netForPct || 0;
  const preview = method === 'goodwill' ? round2(toNum(goodwill)) : round2(netForPct * toNum(percentage) / 100);
  return (
    <div data-testid={`${testIdPrefix}-block`} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold text-white">{title}</h3>
        </div>
        {badge && <span className="text-[10px] font-mono text-slate-500">{badge}</span>}
      </div>

      {/* Raw amounts read-only */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        {rawFields.rows.map((row) => (
          <div key={row.label} className="p-2 rounded-lg bg-slate-950/60 border border-slate-800">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{row.label}</div>
            <div className={`font-mono ${row.emphasize ? 'text-teal-300' : 'text-slate-200'}`}>₹{fmtINR(row.value)}</div>
          </div>
        ))}
      </div>

      {/* Method Toggle */}
      <div className="inline-flex items-center bg-slate-950 border border-slate-800 rounded-xl p-1" data-testid={`${testIdPrefix}-method-toggle`}>
        {[
          { key: 'percentage', label: 'Percentage', Icon: Percent },
          { key: 'goodwill', label: 'Goodwill', Icon: HandCoins },
        ].map(({ key, label, Icon }) => {
          const active = method === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange({ method: key })}
              data-testid={`${testIdPrefix}-method-${key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                active ? 'bg-teal-500 text-slate-950' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Input */}
      {method === 'goodwill' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Goodwill / Direct Amount</label>
            <div className="relative">
              <IndianRupee className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="number" min="0" step="0.01"
                value={goodwill}
                onChange={(e) => onChange({ goodwill: e.target.value })}
                data-testid={`${testIdPrefix}-goodwill-input`}
                className="w-full pl-8 pr-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-sm text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Referral Amount (Preview)</label>
            <div data-testid={`${testIdPrefix}-preview`} className="font-mono text-teal-300 text-lg font-semibold">₹{fmtINR(preview)}</div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Referral Percentage</label>
            <div className="relative">
              <input
                type="number" min="0" max="100" step="0.01"
                value={percentage}
                onChange={(e) => onChange({ percentage: e.target.value })}
                data-testid={`${testIdPrefix}-percentage-input`}
                className="w-full pl-3 pr-8 py-2 bg-slate-950 border border-slate-800 rounded-lg text-sm text-white"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">%</span>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Referral Amount (Preview)</label>
            <div data-testid={`${testIdPrefix}-preview`} className="font-mono text-teal-300 text-lg font-semibold">₹{fmtINR(preview)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ManagerCalculationsPage() {
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [pending, setPending] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [ref, setRef] = useState(null); // full referral doc
  const [loadingRef, setLoadingRef] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [saved, setSaved] = useState(false);

  // Per-section calc state
  const [scan, setScan] = useState({ method: 'percentage', percentage: '', goodwill: '' });
  const [lab, setLab] = useState({ method: 'percentage', percentage: '', goodwill: '' });
  const [pharm, setPharm] = useState({ method: 'percentage', percentage: '', goodwill: '' });
  const [ip, setIp] = useState({ method: 'percentage', percentage: '', goodwill: '' });
  const [procCalcs, setProcCalcs] = useState({}); // {procId: {method, percentage, goodwill}}

  // Ref to the mobile inline expansion so we can smooth-scroll it into view on tap
  const mobilePanelRef = useRef(null);
  useEffect(() => {
    if (!selectedId) return;
    // Only auto-scroll on small screens where the accordion is used
    if (typeof window === 'undefined' || window.innerWidth >= 1024) return;
    const t = setTimeout(() => {
      mobilePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => clearTimeout(t);
  }, [selectedId]);

  const loadPending = async () => {
    try {
      setError('');
      setLoadingList(true);
      const res = await axios.get(`${API_BASE}/referrals?limit=200`, { headers: getAuthHeader() });
      setPending(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load referrals.');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadPending(); /* eslint-disable-next-line */ }, []);

  const loadReferral = async (id) => {
    try {
      setSelectedId(id);
      setLoadingRef(true);
      setSaved(false);
      const res = await axios.get(`${API_BASE}/referrals/${id}`, { headers: getAuthHeader() });
      const r = res.data;
      setRef(r);
      // Seed calc state from stored values
      setScan({ method: r.scan_method || 'percentage', percentage: r.scan_percentage || '', goodwill: r.scan_method === 'goodwill' ? r.scan_referral_amount || '' : '' });
      setLab({ method: r.lab_method || 'percentage', percentage: r.lab_percentage || '', goodwill: r.lab_goodwill_amount || '' });
      setPharm({ method: r.pharmacy_method || 'percentage', percentage: r.pharmacy_percentage || '', goodwill: r.pharmacy_goodwill_amount || '' });
      setIp({ method: r.ip_method || 'percentage', percentage: r.ip_percentage || '', goodwill: r.ip_goodwill_amount || '' });
      const pc = {};
      (r.procedures || []).forEach(p => {
        pc[p.id] = {
          method: p.referral_method || 'percentage',
          percentage: p.referral_percentage || '',
          goodwill: p.referral_method === 'goodwill' ? p.referral_amount || '' : '',
        };
      });
      setProcCalcs(pc);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load referral details.');
    } finally {
      setLoadingRef(false);
    }
  };

  const filteredList = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return pending;
    return pending.filter(r =>
      (r.patient_name || '').toLowerCase().includes(t)
      || (r.referral_id || '').toLowerCase().includes(t)
      || (r.op_number || '').toLowerCase().includes(t)
      || (r.referral_doctor_name || '').toLowerCase().includes(t)
    );
  }, [pending, q]);

  // Live preview of final total
  const finalPreview = useMemo(() => {
    if (!ref) return 0;
    const scanRef = scan.method === 'goodwill' ? toNum(scan.goodwill) : round2(toNum(ref.scan_total_amount) * toNum(scan.percentage) / 100);
    const labRef = lab.method === 'goodwill' ? toNum(lab.goodwill) : round2(toNum(ref.lab_net_amount) * toNum(lab.percentage) / 100);
    const phRef = pharm.method === 'goodwill' ? toNum(pharm.goodwill) : round2(toNum(ref.pharmacy_net_amount) * toNum(pharm.percentage) / 100);
    const ipRef = ip.method === 'goodwill' ? toNum(ip.goodwill) : round2(toNum(ref.ip_net_amount) * toNum(ip.percentage) / 100);
    let procTotal = 0;
    (ref.procedures || []).forEach(p => {
      const c = procCalcs[p.id] || { method: 'percentage', percentage: 0, goodwill: 0 };
      if (c.method === 'goodwill') procTotal += toNum(c.goodwill);
      else procTotal += round2(toNum(p.procedure_amount) * toNum(c.percentage) / 100);
    });
    return round2(scanRef + labRef + phRef + ipRef + procTotal);
  }, [ref, scan, lab, pharm, ip, procCalcs]);

  const handleSave = async () => {
    if (!ref) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const body = {
        scan: { method: scan.method, percentage: toNum(scan.percentage), goodwill_amount: toNum(scan.goodwill) },
        lab: { method: lab.method, percentage: toNum(lab.percentage), goodwill_amount: toNum(lab.goodwill) },
        pharmacy: { method: pharm.method, percentage: toNum(pharm.percentage), goodwill_amount: toNum(pharm.goodwill) },
        ip: { method: ip.method, percentage: toNum(ip.percentage), goodwill_amount: toNum(ip.goodwill) },
        procedures: (ref.procedures || []).map(p => {
          const c = procCalcs[p.id] || { method: 'percentage', percentage: 0, goodwill: 0 };
          return { id: p.id, method: c.method, percentage: toNum(c.percentage), goodwill_amount: toNum(c.goodwill) };
        }),
      };
      const res = await axios.patch(`${API_BASE}/referrals/${ref.id}/calculations`, body, { headers: getAuthHeader() });
      setRef(res.data.referral);
      setSaved(true);
      // Refresh list to reflect updated final amount
      await loadPending();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save calculations.');
    } finally {
      setSaving(false);
    }
  };

  // Shared workspace JSX (used inline on mobile under the active row and in the right column on desktop)
  const workspaceEl = (
    <>
      {!selectedId && (
        <div className="p-10 sm:p-16 text-center bg-slate-900/60 border border-slate-800 rounded-2xl">
          <Calculator className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Select a referral to review and calculate its referral amounts.</p>
        </div>
      )}
      {selectedId && loadingRef && (
        <div className="p-10 sm:p-16 text-center bg-slate-900/60 border border-slate-800 rounded-2xl">
          <Loader2 className="w-6 h-6 text-teal-400 animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Loading referral…</p>
        </div>
      )}
      {selectedId && ref && !loadingRef && (
        <div className="space-y-4">
                {/* Patient summary */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-[10px] uppercase tracking-widest text-teal-400 font-semibold font-mono">{ref.referral_id}</div>
                    <h2 className="text-xl font-bold text-white mt-0.5">{ref.patient_name}</h2>
                    <div className="text-xs text-slate-400 mt-1 space-x-3">
                      <span>OP: {ref.op_number}</span>
                      {ref.referral_date && <span>· Date: {ref.referral_date}</span>}
                      <span>· {ref.village || '—'}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      Doctor: <span className="text-slate-200">{ref.referral_doctor_name}</span>
                      {' · '}Exec: <span className="text-slate-200">{ref.marketing_executive_name}</span>
                      {' · '}Consultant: <span className="text-slate-200">{ref.consultant_name}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Final (preview)</div>
                    <div data-testid="manager-final-preview" className="text-2xl font-bold text-teal-300 font-mono">₹{fmtINR(finalPreview)}</div>
                    <div className="text-[10px] text-slate-500">stored: ₹{fmtINR(ref.final_referral_amount)}</div>
                  </div>
                </div>

                {/* Scan */}
                <SectionCalc
                  title="Scan"
                  icon={<ScanLine className="w-4 h-4 text-teal-400" />}
                  badge={`Total ₹${fmtINR(ref.scan_total_amount)}`}
                  rawFields={{
                    netForPct: ref.scan_total_amount || 0,
                    rows: [
                      { label: 'Scan Total', value: ref.scan_total_amount, emphasize: true },
                    ],
                  }}
                  calcState={scan}
                  onChange={(patch) => setScan(s => ({ ...s, ...patch }))}
                  testIdPrefix="scan-calc"
                />

                {/* Lab */}
                <SectionCalc
                  title="Laboratory"
                  icon={<Beaker className="w-4 h-4 text-fuchsia-400" />}
                  badge={`Net ₹${fmtINR(ref.lab_net_amount)}`}
                  rawFields={{
                    netForPct: ref.lab_net_amount || 0,
                    rows: [
                      { label: 'Lab Total', value: ref.lab_total_amount },
                      { label: 'Kit Cost', value: ref.lab_kit_amount },
                      { label: 'Net', value: ref.lab_net_amount, emphasize: true },
                    ],
                  }}
                  calcState={lab}
                  onChange={(patch) => setLab(s => ({ ...s, ...patch }))}
                  testIdPrefix="lab-calc"
                />

                {/* Pharmacy */}
                <SectionCalc
                  title="Pharmacy"
                  icon={<Pill className="w-4 h-4 text-emerald-400" />}
                  badge={`Net ₹${fmtINR(ref.pharmacy_net_amount)}`}
                  rawFields={{
                    netForPct: ref.pharmacy_net_amount || 0,
                    rows: [
                      { label: 'Pharmacy Total', value: ref.pharmacy_total_amount },
                      { label: 'Kit Cost', value: ref.pharmacy_kit_amount },
                      { label: 'Net', value: ref.pharmacy_net_amount, emphasize: true },
                    ],
                  }}
                  calcState={pharm}
                  onChange={(patch) => setPharm(s => ({ ...s, ...patch }))}
                  testIdPrefix="pharmacy-calc"
                />

                {/* IP */}
                <SectionCalc
                  title="IP"
                  icon={<BedDouble className="w-4 h-4 text-amber-400" />}
                  badge={`Net ₹${fmtINR(ref.ip_net_amount)}`}
                  rawFields={{
                    netForPct: ref.ip_net_amount || 0,
                    rows: [
                      { label: 'IP Amount', value: ref.ip_amount },
                      { label: 'Deductibles', value: ref.ip_deductibles },
                      { label: 'Net', value: ref.ip_net_amount, emphasize: true },
                    ],
                  }}
                  calcState={ip}
                  onChange={(patch) => setIp(s => ({ ...s, ...patch }))}
                  testIdPrefix="ip-calc"
                />

                {/* Procedures */}
                {(ref.procedures || []).length > 0 && (
                  <div data-testid="manager-procedures-block" className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <ClipboardList className="w-4 h-4 text-cyan-400" />
                      <h3 className="text-sm font-semibold text-white">Procedures</h3>
                    </div>
                    <div className="space-y-3">
                      {ref.procedures.map(p => {
                        const c = procCalcs[p.id] || { method: 'percentage', percentage: '', goodwill: '' };
                        const preview = c.method === 'goodwill' ? round2(toNum(c.goodwill)) : round2(toNum(p.procedure_amount) * toNum(c.percentage) / 100);
                        return (
                          <div key={p.id} className="p-3 rounded-xl bg-slate-950/40 border border-slate-800 flex flex-wrap gap-3 items-center">
                            <div className="flex-1 min-w-[160px]">
                              <div className="text-sm font-medium text-white">{p.procedure_name}</div>
                              <div className="text-[10px] text-slate-500 font-mono">₹{fmtINR(p.procedure_amount)}</div>
                            </div>
                            <div className="inline-flex items-center bg-slate-900 border border-slate-800 rounded-lg p-0.5">
                              {['percentage', 'goodwill'].map(m => (
                                <button
                                  key={m}
                                  type="button"
                                  data-testid={`proc-${p.id}-method-${m}`}
                                  onClick={() => setProcCalcs(pc => ({ ...pc, [p.id]: { ...(pc[p.id] || {}), method: m } }))}
                                  className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all ${
                                    c.method === m ? 'bg-teal-500 text-slate-950' : 'text-slate-400 hover:text-white'
                                  }`}
                                >
                                  {m === 'percentage' ? '%' : 'Goodwill'}
                                </button>
                              ))}
                            </div>
                            {c.method === 'goodwill' ? (
                              <div className="w-32">
                                <input
                                  type="number" min="0" step="0.01"
                                  value={c.goodwill}
                                  onChange={(e) => setProcCalcs(pc => ({ ...pc, [p.id]: { ...c, goodwill: e.target.value } }))}
                                  data-testid={`proc-${p.id}-goodwill`}
                                  className="w-full px-2 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-sm text-white text-right"
                                  placeholder="₹0"
                                />
                              </div>
                            ) : (
                              <div className="w-24 relative">
                                <input
                                  type="number" min="0" max="100" step="0.01"
                                  value={c.percentage}
                                  onChange={(e) => setProcCalcs(pc => ({ ...pc, [p.id]: { ...c, percentage: e.target.value } }))}
                                  data-testid={`proc-${p.id}-percentage`}
                                  className="w-full pl-2 pr-6 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-sm text-white text-right"
                                  placeholder="0"
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">%</span>
                              </div>
                            )}
                            <div className="text-right w-24">
                              <div className="text-[10px] text-slate-500">Referral</div>
                              <div className="font-mono text-teal-300 font-semibold">₹{fmtINR(preview)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Save */}
                <div className="sticky bottom-4 z-20 flex items-center justify-between gap-3 p-4 rounded-2xl bg-slate-950/95 border border-slate-800 backdrop-blur-xl shadow-2xl">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Final Referral</div>
                    <div className="text-xl font-bold text-teal-300 font-mono">₹{fmtINR(finalPreview)}</div>
                  </div>
                  {saved && (
                    <div className="text-xs text-emerald-300 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Saved</div>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="manager-save-calc-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-slate-950 font-semibold text-sm rounded-xl transition-all"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Calculations
                  </button>
                </div>
              </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Title */}
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal-400 font-semibold">
            <Calculator className="w-3.5 h-3.5" />
            Manager Workspace
          </div>
          <h1 data-testid="manager-calc-title" className="mt-1 text-3xl sm:text-4xl font-bold text-white tracking-tight">Referral Calculations</h1>
          <p className="text-sm text-slate-400 mt-1">Review reception, lab and pharmacy entries then set the referral percentage or goodwill for each section.</p>
        </div>

        {error && (
          <div data-testid="manager-calc-error" className="p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-red-200 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          {/* Left: referral list (also hosts the inline accordion workspace on mobile) */}
          <aside className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] flex flex-col">
            <div className="relative mb-3">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="manager-search-input"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search patient, referral ID, OP, doctor…"
                className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
              />
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 px-1">
              {loadingList ? 'Loading…' : `${filteredList.length} referral(s)`}
            </div>
            <div className="flex-1 lg:overflow-y-auto space-y-2 pr-1">
              {filteredList.map(r => {
                const active = selectedId === r.id;
                return (
                  <div key={r.id} className="space-y-2">
                    <button
                      type="button"
                      onClick={() => (active ? setSelectedId(null) : loadReferral(r.id))}
                      data-testid={`manager-ref-row-${r.referral_id}`}
                      aria-expanded={active}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        active ? 'bg-teal-500/10 border-teal-500/40' : 'bg-slate-950/40 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[11px] text-teal-400">{r.referral_id}</span>
                        <span className={`text-[10px] uppercase tracking-wider ${r.payment_status === 'paid' ? 'text-emerald-300' : 'text-amber-300'}`}>{r.payment_status}</span>
                      </div>
                      <div className="text-sm text-white truncate mt-1">{r.patient_name}</div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-1">
                        <User className="w-3 h-3" />
                        <span className="truncate">{r.referral_doctor_name || '—'}</span>
                        {r.referral_date && (<><Calendar className="w-3 h-3 ml-2" /><span>{r.referral_date}</span></>)}
                      </div>
                      <div className="mt-1 text-right text-xs font-semibold text-white">₹{fmtINR(r.final_referral_amount)}</div>
                    </button>

                    {/* Mobile inline accordion: expands directly under the tapped patient */}
                    {active && (
                      <div
                        ref={mobilePanelRef}
                        data-testid={`manager-ref-panel-${r.referral_id}`}
                        className="lg:hidden animate-in fade-in slide-in-from-top-2 duration-300"
                      >
                        {workspaceEl}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredList.length === 0 && !loadingList && (
                <p className="text-center text-xs text-slate-500 py-8">No referrals match.</p>
              )}
            </div>
          </aside>

          {/* Right: workspace (desktop only) */}
          <section className="hidden lg:block">
            {workspaceEl}
          </section>
        </div>
      </main>
    </div>
  );
}
